import { useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  CATEGORIES,
  CATEGORY_META,
  PRIORITIES,
  type Category,
  type ListItem,
  type Priority,
} from '@travel-planner/shared';
import { useListItems, useReorderItems } from './useListItems';
import { Column } from './Column';
import { CategoryBadge } from './itemUi';

type Cols = Record<Priority, ListItem[]>;

function group(items: ListItem[]): Cols {
  const cols: Cols = { must: [], should: [], could: [] };
  for (const it of [...items].sort((a, b) => a.order - b.order)) cols[it.priority].push(it);
  return cols;
}

export function Board({ tripId, countryId }: { tripId: string; countryId: string }) {
  const { data: items = [], isLoading } = useListItems(tripId, countryId);
  const reorder = useReorderItems(tripId, countryId);

  const [cols, setCols] = useState<Cols>(() => group(items));
  const [activeId, setActiveId] = useState<string | null>(null);
  const [origin, setOrigin] = useState<Priority | null>(null);

  // Resync from the server whenever data changes and we're not mid-drag.
  useEffect(() => {
    if (activeId === null) setCols(group(items));
  }, [items, activeId]);

  const [hidden, setHidden] = useState<Set<Category>>(new Set());
  const filterActive = hidden.size > 0;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const findContainer = (id: string): Priority | null => {
    if (id.startsWith('col:')) return id.slice(4) as Priority;
    return PRIORITIES.find((p) => cols[p].some((i) => i.id === id)) ?? null;
  };

  const activeItem = activeId
    ? PRIORITIES.flatMap((p) => cols[p]).find((i) => i.id === activeId) ?? null
    : null;

  function onDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    setActiveId(id);
    setOrigin(findContainer(id));
  }

  // Move an item between columns live as it's dragged over a new column.
  function onDragOver(e: DragOverEvent) {
    const id = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId) return;
    const from = findContainer(id);
    const to = findContainer(overId);
    if (!from || !to || from === to) return;

    setCols((prev) => {
      const fromItems = prev[from];
      const moving = fromItems.find((i) => i.id === id);
      if (!moving) return prev;
      const toItems = prev[to];
      const overIndex = overId.startsWith('col:')
        ? toItems.length
        : toItems.findIndex((i) => i.id === overId);
      const insertAt = overIndex < 0 ? toItems.length : overIndex;
      return {
        ...prev,
        [from]: fromItems.filter((i) => i.id !== id),
        [to]: [
          ...toItems.slice(0, insertAt),
          { ...moving, priority: to },
          ...toItems.slice(insertAt),
        ],
      };
    });
  }

  function onDragEnd(e: DragEndEvent) {
    const id = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    const startedIn = origin;
    setActiveId(null);
    setOrigin(null);

    if (!overId) {
      setCols(group(items));
      return;
    }
    const dest = findContainer(id);
    if (!dest) return;

    const destItems = cols[dest];
    const oldIndex = destItems.findIndex((i) => i.id === id);
    const newIndex = overId.startsWith('col:')
      ? destItems.length - 1
      : destItems.findIndex((i) => i.id === overId);
    if (oldIndex < 0 || newIndex < 0) return;

    const newState: Cols = { ...cols, [dest]: arrayMove(destItems, oldIndex, newIndex) };
    setCols(newState);

    // Persist destination, plus the source column if the item changed columns.
    reorder.mutate({ countryId, priority: dest, orderedIds: newState[dest].map((i) => i.id) });
    if (startedIn && startedIn !== dest) {
      reorder.mutate({
        countryId,
        priority: startedIn,
        orderedIds: newState[startedIn].map((i) => i.id),
      });
    }
  }

  function toggleCategory(c: Category) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  const visible = (list: ListItem[]) => list.filter((i) => !hidden.has(i.category));

  if (isLoading) return <p className="text-secondary">Loading lists…</p>;

  return (
    <div>
      <CategoryFilter hidden={hidden} onToggle={toggleCategory} onClear={() => setHidden(new Set())} />
      {filterActive && (
        <p className="text-body-tertiary my-1" style={{ fontSize: '0.8rem' }}>
          Drag-reordering is paused while a category filter is active.
        </p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={() => {
          setActiveId(null);
          setOrigin(null);
          setCols(group(items));
        }}
      >
        <div className="board-columns">
          {PRIORITIES.map((p) => (
            <Column
              key={p}
              priority={p}
              items={visible(cols[p])}
              tripId={tripId}
              countryId={countryId}
              dragDisabled={filterActive}
            />
          ))}
        </div>
        <DragOverlay>
          {activeItem ? (
            <div
              style={{
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--surface)',
                padding: 10,
                boxShadow: 'var(--shadow-lg)',
              }}
            >
              {activeItem.title}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function CategoryFilter({
  hidden,
  onToggle,
  onClear,
}: {
  hidden: Set<Category>;
  onToggle: (c: Category) => void;
  onClear: () => void;
}) {
  return (
    <div className="d-flex gap-2 flex-wrap align-items-center mb-3">
      {CATEGORIES.map((c) => {
        const on = !hidden.has(c);
        return (
          <button
            key={c}
            onClick={() => onToggle(c)}
            title={on ? 'Click to hide' : 'Click to show'}
            className="btn p-0 border-0"
            style={{ background: 'transparent', minHeight: 'auto', opacity: on ? 1 : 0.35 }}
          >
            <CategoryBadge category={c} />
          </button>
        );
      })}
      {hidden.size > 0 && (
        <button onClick={onClear} className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem' }}>
          Show all
        </button>
      )}
    </div>
  );
}
